import { isEmpty, cloneDeep } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import Sticky from 'react-sticky-el'
import { Container, Row, Col } from 'reactstrap'
import { fetchTax, fetchShippingQuote, createToken, clearPromotions, fetchCartItems, fetchUser } from '../../actions'
import { cartItemsRequireCheckForApproval, returnCartItemsRequireApprovalError } from '../Cart/cartFunctions'
import CartItemsList from '../Cart/CartItemsList'
import LoadingSpinner from '../general/LoadingSpinner'
import CheckoutAddressSelection from './CheckoutAddressSelection'
import CheckoutCardPanel from './CheckoutCardPanel'
import CheckoutCreditBalances from './CheckoutCreditBalances'
import CheckoutErrorsPanel from './CheckoutErrorsPanel'
import {
  setErrors,
  setShippingLoading,
  checkoutOrderCleanup,
  setCheckoutLoading,
  setTaxLoading,
  resolveUnitPrice
} from './checkoutFunctions'
import CheckoutInformation from './CheckoutInformation'
import OrderSummary from './CheckoutOrderSummary'
import PromotionsPanel from './CheckoutPromotionsPanel'
import GuestCheckoutPanel from './GuestCheckoutPanel'

/**
 * CheckoutPage component
 * @param {object} props - Component props
 * @param {object} props.checkout - Checkout state
 * @param {object} props.errors - Errors state
 * @param {object} props.portal - Portal state
 * @param {object} props.userCredits - User credits state
 * @param {Array} props.customer_addresses - Customer addresses
 * @param {object} props.managed_customer_addresses - Managed customer addresses
 * @param {object} props.location - Location state
 * @param {object} props.currentLocation - Current location state
 * @param {Array} props.cart_multiple_managed_customer_addresses - Cart multiple managed customer addresses
 * @param {Array} props.appliedPromotions - Applied promotions
 * @param {Array} props.cartItems - Cart items
 * @param {object} props.selectedCustomerAddress - Selected customer address
 * @param {boolean} props.cart_loading - Cart loading state
 * @param {object} props.tax - Tax state
 * @param {object} props.checkoutErrors - Checkout errors
 * @param {object} props.currentUser - Current user state
 * @param {number} props.subtotal - Subtotal
 * @param {Array} props.checkoutForm - Checkout form
 * @param {function} props.fetchTax - Fetch tax action
 * @param {function} props.fetchShippingQuote - Fetch shipping quote action
 * @param {function} props.setSubtotal - Set subtotal action
 * @param {function} props.createToken - Create token action
 * @param {function} props.setErrors - Set errors action
 * @param {function} props.setShippingLoading - Set shipping loading action
 * @param {function} props.checkoutOrderCleanup - Checkout order cleanup action
 * @param {function} props.setCheckoutLoading - Set checkout loading action
 * @param {function} props.clearPromotions - Clear promotions action
 * @param {function} props.fetchCartItems - Fetch cart items action
 * @param {function} props.setTaxLoading - Set tax loading action
 * @param {function} props.fetchUserCredit - Fetch user credit action
 * @param {function} props.fetchUser - Fetch user action
 * @param {function} props.returnCartItemsRequireApprovalError - Return cart items require approval error action
 */
const CheckoutPage = props => {
  // State declarations
  const [isZoomedIn, setIsZoomedIn] = useState(false)

  // Helper functions
  const addTaxablePriceToCartItems = cartItems => {
    const incomingCartItems = cloneDeep(cartItems)

    return incomingCartItems.map(cartItem => {
      // taxable price is unit price minus discounts
      // pass true to second arg to include any discount
      const cartItemUnitPriceCents = resolveUnitPrice(cartItem.priceData, true)

      return {
        name: cartItem.name,
        product_id: cartItem.product_id,
        quantity: cartItem.quantity,
        taxable_price: cartItemUnitPriceCents,
        cart_item_id: cartItem.cart_item_id
      }
    })
  }

  const fetchTaxQuote = () => {
    if (props.cartItems.length === 0) return

    props.setTaxLoading(true)

    const formData = new FormData()
    const customer_address = props.checkout.selectedCustomerAddress

    const cartItemsWithTaxablePrice = addTaxablePriceToCartItems(props.cartItems)

    formData.append('customer_address', JSON.stringify(customer_address))
    formData.append('cart_items', JSON.stringify(cartItemsWithTaxablePrice))
    formData.append('tax_adjustment', props.checkout.taxReduction)
    formData.append('shipment_in_cents', props.checkout.checkoutData?.shipping)

    // if portal is multi address, send address selections
    if (props.portal.multiple_address_shipping && props.currentUser.id) {
      formData.append('multi_address_selection', JSON.stringify(props.checkout.multiAddressSelection))
    }

    if (props.appliedPromotions.length > 0) {
      const updatedCart = props.appliedPromotions[0].updated_cart
      const updatedCartWithTaxablePrice = updatedCart.map(cartItem => ({
        ...cartItem,
        taxable_price: cartItem.promo_data.discounted_price
      }))

      formData.append('promotion_id', props.appliedPromotions[0].promotion_id)
      formData.append('subtotal_reduction', props.checkout.subtotalReduction)
      formData.append('shipping_reduction', props.checkout.shippingReduction)
      formData.append('shipping_discount_in_cents', props.checkout.shippingReductionEncrypted)
      formData.append('product_reduction', props.checkout.productReduction)
      formData.append('updated_cart', JSON.stringify(updatedCartWithTaxablePrice))
    }

    if (props.managed_customer_addresses) {
      formData.append('epcid_zero', props.managed_customer_addresses.epcid)
    }

    props.fetchTax(formData).finally(() => {
      props.setTaxLoading(false)
    })
  }

  // Detect zoom level and set state
  const detectZoom = () => {
    const zoomLevel = Math.round(window.devicePixelRatio * 100)
    if (zoomLevel > 100) {
      setIsZoomedIn(true)
    } else {
      setIsZoomedIn(false)
    }
  }

  // Effects
  useEffect(() => {
    props.setCheckoutLoading(true).then(() => {
      props.checkoutOrderCleanup(props.currentLocation).then(() => {
        props.clearPromotions().then(() => {
          const userId = props.currentUser.id

          window.scrollTo(0, 0)
          props.createToken().then(() => {
            props.fetchCartItems(userId, props.portal, props.currentLocation, props.userCredits.coop_credit.remaining)
          })
        })
      })
    })
  }, [props.currentLocation.id, props.currentUser.id])

  // Handle contactEmail error separately to prevent race conditions
  useEffect(() => {
    if (props.currentUser.id) {
      // Only update contactEmail error if it's currently set to true
      if (props.checkoutErrors.contactEmail) {
        props.setErrors({ contactEmail: false })
      }
    }
  }, [props.currentUser.id, props.checkoutErrors.contactEmail])

  // Clear initial shipping error state when cart items are loaded
  useEffect(() => {
    if (props.cartItems && props.cartItems.length > 0 && props.checkoutErrors.shipping) {
      const allItemsDigitalDelivery = props.cartItems.every(cartItem => cartItem.shippable_type === 'Digital Delivery')

      // If all items are digital delivery, clear shipping error since no shipping is required
      if (allItemsDigitalDelivery) {
        props.setErrors({ shipping: false })
      }
    }
  }, [props.cartItems])

  // Handle legacy shipping errors from the errors reducer (different from checkout errors)
  useEffect(() => {
    const legacyShippingErrorExists =
      props.errors && props.errors.shipping_errors && props.errors.shipping_errors.message

    // Only set shipping error to true if there's a legacy error and no current checkout shipping error
    if (legacyShippingErrorExists && !props.checkoutErrors.shipping) {
      props.setErrors({ shipping: true })
    }
  }, [props.errors.shipping_errors])

  // when any price totals change that affect tax, re-fetch tax quote
  useEffect(() => {
    const multiAddressShippingEnabled = props.portal.multiple_address_shipping && props.currentUser.id

    // For multi-address shipping, make sure each cart item has a selection
    const eachItemHasSelection = multiAddressShippingEnabled
      ? props.cartItems.every(cartItem =>
          props.checkout.multiAddressSelection.map(s => s.cartItemId).includes(cartItem.cart_item_id)
        )
      : true // for single-address shipping, we don't need this condition

    // If there's a selected customer address, a positive subtotal, and the appropriate shipping settings, fetch tax
    if (
      !isEmpty(props.selectedCustomerAddress) &&
      (multiAddressShippingEnabled ? eachItemHasSelection : !multiAddressShippingEnabled)
    ) {
      fetchTaxQuote()
    }
  }, [
    props.checkout.multiAddressSelection,
    props.selectedCustomerAddress,
    props.subtotal,
    props.checkout.shippingQuote,
    props.checkout.subtotalReduction,
    props.checkout.shippingReduction,
    props.checkout.productReduction
  ])

  // when delivery address is selected, fetch shipping quote
  useEffect(() => {
    const guestCheckoutEnabled = props.portal.checkout_mode === 'both' || props.portal.checkout_mode === 'guest_only'
    // since guest checkout ignores multi address shipping, we need to check if guest checkout is enabled
    if (props.portal.multiple_address_shipping && !props.checkout.multiAddressSelection.length && !guestCheckoutEnabled)
      return
    if (!isEmpty(props.selectedCustomerAddress)) {
      props.fetchShippingQuote(props.selectedCustomerAddress, props.cartItems, props.checkout.multiAddressSelection)
    }
  }, [props.selectedCustomerAddress, props.checkout.multiAddressSelection])

  // Check if cart items contain both requires approval and does not require approval
  useEffect(() => {
    if (
      props.cartItems &&
      props.cartItems.length > 0 &&
      cartItemsRequireCheckForApproval(null, props.cartItems, props.currentLocation)
    ) {
      props.returnCartItemsRequireApprovalError(props.cartItems, null)
    }
  }, [props.cartItems])

  useEffect(() => {
    window.addEventListener('resize', detectZoom)
    return () => window.removeEventListener('resize', detectZoom)
  }, [detectZoom])

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 992px)' })

  const fixedHeaderElement = document.querySelector('.iron-fixed-header')
  const menuHeight = fixedHeaderElement !== null ? fixedHeaderElement.offsetHeight : '100px'

  const screenHeight = window.screen.availHeight
  const responseHeight = screenHeight !== null && screenHeight <= 770 ? true : false

  const showCheckoutInformation = props.currentLocation.show_checkout_information && props.checkoutForm.length > 0

  return (
    <div>
      <Container className="mt-30 mb-30">
        <Row>
          <Col xs={12} md={7} lg={8}>
            {showCheckoutInformation ? <CheckoutInformation /> : null}
            <CheckoutErrorsPanel />
            <GuestCheckoutPanel />
            <CheckoutAddressSelection />
            {props.cart_loading ? <LoadingSpinner /> : <CheckoutCreditBalances />}
            <CheckoutCardPanel extraFields={props.currentUser.run_automation_and_extra_fields} />
            <CartItemsList isCheckout={true} />
          </Col>
          <Col xs={12} md={5} lg={4} className="right-panel-promotions-summary">
            <Sticky
              className="sticky-right-panel"
              topOffset={0}
              stickyStyle={{
                top: menuHeight >= 90 ? '160px' : '100px',
                zIndex: 2
              }}
              disabled={isTabletOrMobile || responseHeight || isZoomedIn}
            >
              {props.currentUser.id && <PromotionsPanel />}
              <OrderSummary isCheckout={true} />
            </Sticky>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

CheckoutPage.propTypes = {
  checkout: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  portal: PropTypes.object.isRequired,
  userCredits: PropTypes.object.isRequired,
  customer_addresses: PropTypes.array.isRequired,
  managed_customer_addresses: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  currentLocation: PropTypes.object.isRequired,
  cart_multiple_managed_customer_addresses: PropTypes.array.isRequired,
  appliedPromotions: PropTypes.array.isRequired,
  cartItems: PropTypes.array.isRequired,
  selectedCustomerAddress: PropTypes.object.isRequired,
  cart_loading: PropTypes.bool.isRequired,
  tax: PropTypes.object.isRequired,
  checkoutErrors: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  subtotal: PropTypes.number.isRequired,
  checkoutForm: PropTypes.array.isRequired,
  fetchTax: PropTypes.func.isRequired,
  fetchShippingQuote: PropTypes.func.isRequired,
  setSubtotal: PropTypes.func.isRequired,
  createToken: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  setShippingLoading: PropTypes.func.isRequired,
  checkoutOrderCleanup: PropTypes.func.isRequired,
  setCheckoutLoading: PropTypes.func.isRequired,
  clearPromotions: PropTypes.func.isRequired,
  fetchCartItems: PropTypes.func.isRequired,
  setTaxLoading: PropTypes.func.isRequired,
  fetchUserCredit: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  returnCartItemsRequireApprovalError: PropTypes.func.isRequired
}

const mapStateToProps = state => {
  return {
    checkout: state.checkout,
    errors: state.errors,
    portal: state.portal,
    userCredits: state.userCredits,
    managed_customer_addresses: state.managed_customer_addresses,
    currentLocation: state.currentLocation,
    appliedPromotions: state.checkout.appliedPromotions,
    cartItems: state.cartItems,
    selectedCustomerAddress: state.checkout.selectedCustomerAddress,
    cart_loading: state.checkout.cart_loading,
    checkoutErrors: state.checkout.errors,
    currentUser: state.currentUser,
    subtotal: state.checkout.subtotal,
    checkoutForm: state.checkoutForm
  }
}

export default connect(mapStateToProps, {
  fetchTax,
  fetchShippingQuote,
  createToken,
  setErrors,
  setShippingLoading,
  checkoutOrderCleanup,
  setCheckoutLoading,
  clearPromotions,
  fetchCartItems,
  setTaxLoading,
  fetchUser,
  returnCartItemsRequireApprovalError
})(CheckoutPage)
